/**
 * PlaylistSearchTileV1 — Infinity Carousel.
 *
 * Hero composition: thumbnails cycle continuously through a horizontal
 * slot. Front-of-cycle card sits centered at scale(1.05) and full
 * opacity. Cards drift leftward, shrinking and fading; near the left
 * edge they hit opacity 0 and instantly wrap back to the right
 * offscreen position to re-enter the cycle.
 *
 * Animation duration: 12 s for a full lap (variant 1 default). All cards
 * share the same `playlist-search-tile-v1-infinity` animation,
 * distributed evenly via inline `animation-delay` so the hero always has
 * one card centered.
 *
 * Variants: pass `variantIndex` (1-10) to swap the animation feel.
 *   1  — Slow Linear (default, current behavior)
 *   2  — Fast Drift
 *   3  — Smooth Ease
 *   4  — Reversed Direction
 *   5  — Vertical Drift
 *   6  — Rotating Drift
 *   7  — Elastic
 *   8  — Tight Pack
 *   9  — Center Hold
 *   10 — Diagonal Drift
 *
 * Empty array     → static <StackedCards /> placeholder, no motion.
 * Single thumb    → static centered card at scale(1.05), no motion.
 * Two+ thumbs     → animated infinity cycle (per variant).
 *
 * Hover           → `animation-play-state: paused` (see SCSS).
 * Reduced motion  → static fanned stack fallback, no animation.
 */

import React from 'react'
import StackedCards from '../../../../Icon/icons/StackedCards/index'
import type { PlaylistSearchTileProps } from '../../shared/types'
import PlaylistTileShell from '../../shared/PlaylistTileShell'
import PlaylistTileHoverBodyV1 from './PlaylistTileHoverBodyV1'

const ANIMATION_DURATION_S = 12

export interface PlaylistSearchTileV1Props extends PlaylistSearchTileProps {
  /** 1-10. Selects which animation variant to render. Defaults to 1
   * (Slow Linear) — preserves existing behavior. */
  variantIndex?: number
}

interface InfinityCarouselProps {
  thumbnails: string[]
  variantIndex: number
}

function InfinityCarousel (
  { thumbnails, variantIndex }: InfinityCarouselProps,
): React.ReactElement {
  const stageClassName = `playlist-search-tile-v1__stage playlist-search-tile-v1--variant-${variantIndex}`

  if (thumbnails.length === 0) {
    return (
      <div className="playlist-search-tile-v1__placeholder">
        <StackedCards size="40px" color="rgba(255, 255, 255, 0.65)" />
      </div>
    )
  }

  if (thumbnails.length === 1) {
    return (
      <div className={stageClassName}>
        <div className="playlist-search-tile-v1__card playlist-search-tile-v1__card--static">
          <img
            src={thumbnails[0]}
            alt=""
            loading="lazy"
            className="playlist-search-tile-v1__img"
          />
        </div>
      </div>
    )
  }

  const total = thumbnails.length
  return (
    <div className={stageClassName}>
      {thumbnails.map((url, i) => {
        const delay = -((i * ANIMATION_DURATION_S) / total)
        return (
          <div
            key={`${url}-${i}`}
            className="playlist-search-tile-v1__card"
            style={{ animationDelay: `${delay}s` }}
          >
            <img
              src={url}
              alt=""
              loading="lazy"
              className="playlist-search-tile-v1__img"
            />
          </div>
        )
      })}
    </div>
  )
}

function PlaylistSearchTileV1 (props: PlaylistSearchTileV1Props): React.ReactElement {
  const { variantIndex = 1, ...shellProps } = props
  return (
    <PlaylistTileShell
      {...shellProps}
      renderHero={({ previewThumbnailUrls }) => (
        <InfinityCarousel
          thumbnails={previewThumbnailUrls}
          variantIndex={variantIndex}
        />
      )}
      renderHoverBody={({
        playlistName,
        metaLine,
        previewThumbnailUrls,
        previewContentTitles,
        itemCount,
      }) => (
        <PlaylistTileHoverBodyV1
          playlistName={playlistName}
          metaLine={metaLine}
          thumbnails={previewThumbnailUrls}
          titles={previewContentTitles}
          itemCount={itemCount}
        />
      )}
    />
  )
}

export default React.memo(PlaylistSearchTileV1)
